import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import React from "react";
import { storagePaginationValues } from "../../utils/constants";

interface PaginationControlsProps {
  currentLimit: number;
  onLimitChange: (limit: number) => void;
  totalItems?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showLimitSelector?: boolean;
  showPageInfo?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  pageTokens?: string[];
  hasNextPage?: boolean;
  borderStyle?: object;
  buttonStyle?: object;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentLimit,
  onLimitChange,
  totalItems,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showLimitSelector = true,
  showPageInfo = true,
  disabled = false,
  size = "medium",
  pageTokens = [],
  hasNextPage = false,
  borderStyle = {},
  buttonStyle = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    const newLimit = event.target.value as number;
    onLimitChange(newLimit);
  };

  const handlePageChange = (page: number) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePageNumberClick = (pageNumber: number) => {
    if (onPageChange && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    }
  };

  const getButtonSize = () => {
    switch (size) {
      case "small":
        return "small";
      case "large":
        return "medium";
      default:
        return "medium";
    }
  };

  const getTypographyVariant = () => {
    switch (size) {
      case "small":
        return "body2";
      case "large":
        return "h6";
      default:
        return "body1";
    }
  };

  const buttonMinWidth = { xs: 32, sm: 40 };
  const from = totalItems != null && totalItems > 0 ? (currentPage - 1) * currentLimit + 1 : 0;
  const to = totalItems != null && totalItems > 0 ? Math.min(currentPage * currentLimit, totalItems) : 0;

  if (isMobile && onPageChange) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 1, sm: 2 },
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          ...borderStyle
        }}
      >
        
        {showLimitSelector && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
              Per page:
            </Typography>
            <FormControl size="small" disabled={disabled}>
              <Select
                value={currentLimit}
                onChange={handleLimitChange}
                sx={{ minWidth: 50, fontSize: "0.75rem" }}
              >
                {storagePaginationValues.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        
        {showPageInfo && totalItems !== undefined && (
          <Box sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              {totalItems > 0 ? `${from}–${to} of ${totalItems}` : "No items"}
            </Typography>
          </Box>
        )}

        
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
            sx={{ p: 0.75 }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", minWidth: 40, textAlign: "center" }}>
            {currentPage}/{totalPages}
          </Typography>
          <IconButton
            size="small"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || disabled || !hasNextPage}
            sx={{ p: 0.75 }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 1 },
        px: { xs: 1, sm: 2 },
        py: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        minWidth: 0,
        ...borderStyle
      }}
    >
      
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexShrink: 0 }}>
        {showLimitSelector && (
          <>
            <Typography variant={getTypographyVariant()} color="text.secondary" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.8125rem", sm: "inherit" } }}>
              Items per page:
            </Typography>
            <FormControl size={getButtonSize()} disabled={disabled}>
              <Select
                value={currentLimit}
                onChange={handleLimitChange}
                sx={{ minWidth: { xs: 56, sm: 64 } }}
              >
                {storagePaginationValues.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      
      {showPageInfo && totalItems !== undefined && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography variant={getTypographyVariant()} color="text.secondary" sx={{ fontSize: { xs: "0.8125rem", sm: "inherit" }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {totalItems > 0
              ? `${from}–${to} of ${totalItems}`
              : "No items to display"}
          </Typography>
        </Box>
      )}

      
      {onPageChange && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexBasis: { xs: "100%", sm: "auto" },
            minWidth: 0,
            justifyContent: { xs: "flex-start", sm: "center" },
            overflowX: "auto",
            overflowY: "hidden",
            pb: { xs: 0.5, sm: 0 },
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { borderRadius: 3, backgroundColor: "rgba(0,0,0,0.2)" },
          }}
        >
          <Typography variant={getTypographyVariant()} color="text.secondary" sx={{ fontSize: { xs: "0.8125rem", sm: "inherit" }, flexShrink: 0 }}>
            Page {currentPage} of {totalPages}
          </Typography>

          <ButtonGroup size={getButtonSize()} sx={{ flexShrink: 0 }}>
            <Tooltip title="First page">
              {currentPage === 1 || disabled ? (
                <span>
                  <Button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || disabled}
                    sx={{
                      minWidth: { xs: 32, sm: 40 },
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      },
                    }}
                  >
                    {"<<"}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || disabled}
                  sx={{
                    minWidth: { xs: 32, sm: 40 },
                    backgroundColor: "transparent",
                    color: "inherit",
                    border: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "action.hover"
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      color: "action.disabled"
                    }
                  }}
                >
                  {"<<"}
                </Button>
              )}
            </Tooltip>

            <Tooltip title="Previous page">
              {currentPage === 1 || disabled ? (
                <span>
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || disabled}
                    sx={{
                      minWidth: { xs: 32, sm: 40 },
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {"<"}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || disabled}
                  sx={{
                    minWidth: { xs: 32, sm: 40 },
                    backgroundColor: "transparent",
                    color: "inherit",
                    border: "none",
                    boxShadow: "none",
                    ...buttonStyle,
                    "&:hover": {
                      backgroundColor: "rgba(31,93,107,0.1)"
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      color: "action.disabled"
                    }

                  }}
                >
                  {"<"}
                </Button>
              )}
            </Tooltip>

            
            {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
              let pageNumber: number;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <Tooltip key={pageNumber} title={`Page ${pageNumber}`}>
                  {disabled ? (
                    <span>
                      <Button
                        onClick={() => handlePageNumberClick(pageNumber)}
                        disabled={disabled}
                        sx={{
                          minWidth: { xs: 32, sm: 40 },
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                          color: currentPage === pageNumber ? "primary.contrastText" : "inherit",
                          border: "none",
                          boxShadow: "none",
                          ...buttonStyle,
                          "&:hover": {
                            backgroundColor: currentPage === pageNumber ? "primary.main" : "action.hover"
                          },
                          "&:disabled": {
                            backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                            color: currentPage === pageNumber ? "primary.contrastText" : "action.disabled"
                          }
                        }}
                      >
                        {pageNumber}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      onClick={() => handlePageNumberClick(pageNumber)}
                      disabled={disabled}
                      sx={{
                        minWidth: { xs: 32, sm: 40 },
                        backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                        color: currentPage === pageNumber ? "primary.contrastText" : "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                          color: currentPage === pageNumber ? "primary.contrastText" : "action.disabled"
                        }
                      }}
                    >
                      {pageNumber}
                    </Button>
                  )}
                </Tooltip>
              );
            })}

            {hasNextPage && (
              <Tooltip title="Next page">
                {currentPage === totalPages || disabled ? (
                  <span>
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || disabled}
                      sx={{
                        minWidth: { xs: 32, sm: 40 },
                        backgroundColor: "transparent",
                        color: "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          color: "action.disabled"
                        }
                      }}
                    >
                      {">"}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || disabled}
                    sx={{
                      minWidth: { xs: 32, sm: 40 },
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      ...buttonStyle,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {">"}
                  </Button>
                )}
              </Tooltip>
            )}

            {hasNextPage && totalPages > currentPage && (
              <Tooltip title="Last page">
                {currentPage === totalPages || disabled ? (
                  <span>
                    <Button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || disabled}
                      sx={{
                        minWidth: { xs: 32, sm: 40 },
                        backgroundColor: "transparent",
                        color: "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          color: "action.disabled"
                        }
                      }}
                    >
                      {">>"}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || disabled}
                    sx={{
                      minWidth: { xs: 32, sm: 40 },
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      ...buttonStyle,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {">>"}
                  </Button>
                )}
              </Tooltip>
            )}
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export default PaginationControls;
